import {
  AudioPlayer,
  ButtonWithModal,
  Card,
  Copy,
  JsonViewModal,
  PermissionCheck,
} from '../../../components';
import { formatDate, getDurationFromSeconds } from '../../../utilities';
import {
  type ConversationAudioFileResponse,
  type ConversationResponse,
} from '../../../api';
import { PERMISSIONS } from '../../../shared/constants/permissions';
import { CONVERSATION_COST_STATISTICS_UNITS } from './const';
import type { ParticipantInfoResponse, SelfCostStatistics } from '../../../api';

type DetailsProps = {
  conversation: ConversationResponse | null;
  participant: ParticipantInfoResponse | null;
  audio: Array<ConversationAudioFileResponse>;
  agentTitle?: string;
  selfCostStatistics?: SelfCostStatistics | null;
  loadingCostStatistics: boolean;
};

export const Details: React.FC<DetailsProps> = ({
  participant,
  conversation,
  audio = [],
  agentTitle,
  selfCostStatistics,
  loadingCostStatistics,
}) => {
  const userName = participant
    ? [participant.first_name, participant.last_name].filter(Boolean).join(' ')
    : '';

  return (
    <Card
      size="medium"
      title="Details"
      withOverflow={false}
      actions={
        participant
          ? [
              <ButtonWithModal
                key="summary"
                label="Conversation Summary"
                content={
                  conversation?.extracted_summary ?? '*No summary available.*'
                }
              />,
              <JsonViewModal
                key="participant"
                value={participant}
                label="Participant Info"
              />,
            ]
          : []
      }
      className="bg-buttonLight w-full"
      contentClassName="pr-2"
    >
      <p className="text-gray-500 dark:text-gray-400 text-tiny mt-md">
        Participants info
      </p>
      <div className="border border-border rounded-card p-md mt-3 bg-buttonLightText">
        <div className="flex justify-between">
          <span className="text-small font-medium">
            Agent: {agentTitle || '-'}
          </span>
        </div>
        <div className="flex justify-between mt-md">
          <div>
            <span className="text-small font-medium text-light">ID: </span>
            <span className="text-small font-medium">
              {conversation?.agent_id || '-'}
            </span>
          </div>
          {!!conversation?.agent_id && <Copy text={conversation.agent_id} />}
        </div>
      </div>
      {/* User name */}
      {!!participant && (
        <div className="border border-border rounded-card p-md mt-3 bg-buttonLightText">
          <span className="text-small font-medium">
            User: {userName || '-'}
          </span>
          {!!participant.id && (
            <div className="flex justify-between mt-md">
              <div>
                <span className="text-small font-medium text-light">ID: </span>
                <span className="text-small font-medium">{participant.id}</span>
              </div>
              <Copy text={participant.id} />
            </div>
          )}
        </div>
      )}
      <p className="text-gray-500 dark:text-gray-400 text-tiny mt-md">
        Timestamps
      </p>
      <div className="border border-border rounded-card p-md mt-3 bg-buttonLightText">
        <div className="flex justify-between">
          <span className="text-small font-medium text-light">Started at:</span>
          <span className="text-small font-medium">
            {formatDate(conversation?.started_at)}
          </span>
        </div>
        <div className="flex justify-between mt-md">
          <span className="text-small font-medium text-light">
            Finished at:
          </span>
          <span className="text-small font-medium">
            {formatDate(conversation?.ended_at)}
          </span>
        </div>
        <div className="flex justify-between mt-md">
          <span className="text-small font-medium text-light">Duration:</span>
          <span className="text-small font-medium">
            {getDurationFromSeconds(conversation?.duration)}
          </span>
        </div>
      </div>

      <p className="text-gray-500 dark:text-gray-400 text-tiny mt-md">Audio</p>
      <div className="border border-border rounded-card p-md mt-3 bg-buttonLightText">
        {audio.length ? (
          audio.map(({ link, fileName }, index) => (
            <div key={fileName} className="flex items-center my-2 gap-x-sm">
              <span className="text-small font-xs text-light">
                Record: {index + 1}
              </span>
              <AudioPlayer src={`${link}`} />
            </div>
          ))
        ) : (
          <div className="text-small font-xs text-light">No audio</div>
        )}
      </div>
      {conversation?.type === 'phone_call' && (
        <>
          <p className="text-gray-500 dark:text-gray-400 text-tiny mt-md">
            Phones
          </p>
          <div className="border border-border rounded-card p-md mt-3 bg-buttonLightText">
            <div className="flex justify-between mt-md">
              <span className="text-small font-medium text-light">
                User phone:
              </span>
              <span className="text-small font-medium">
                {conversation?.user_phone || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between mt-md">
              <span className="text-small font-medium text-light">
                Inbound SIP phone:
              </span>
              <span className="text-small font-medium">
                {`${conversation?.inbound_phone || 'N/A'}`}
              </span>
            </div>
            <div className="flex justify-between mt-md">
              <span className="text-small font-medium text-light">
                Outbound SIP phone:
              </span>
              <span className="text-small font-medium">
                {conversation?.outbound_phone || 'N/A'}
              </span>
            </div>
          </div>
        </>
      )}

      {selfCostStatistics && !loadingCostStatistics && (
        <PermissionCheck action={PERMISSIONS.VIEW_SELF_COST_DATA}>
          <p className="text-gray-500 dark:text-gray-400 text-tiny mt-md">
            Tokens
          </p>
          <div className="border border-border rounded-card p-md mt-3 bg-buttonLightText">
            <div className="flex flex-col gap-y-md">
              {selfCostStatistics?.units
                ?.filter(unit => unit.unit !== 'room_lifetime_sec')
                .map(unit => (
                  <div className="flex justify-between" key={unit.unit}>
                    <span className="text-small font-medium text-light">
                      {CONVERSATION_COST_STATISTICS_UNITS[
                        unit.unit as keyof typeof CONVERSATION_COST_STATISTICS_UNITS
                      ] || unit.unit}
                      :
                    </span>
                    <span className="text-small font-medium">{unit.value}</span>
                  </div>
                ))}
            </div>
          </div>
        </PermissionCheck>
      )}

      <p className="text-gray-500 dark:text-gray-400 text-tiny mt-md">
        Channels
      </p>
      <div className="border border-border rounded-card p-md mt-3 bg-buttonLightText">
        <span className="text-small font-medium text-light">
          Conversation channel:
        </span>
        <span className="text-small font-medium ml-xs">
          {conversation?.type || ''}
        </span>
      </div>
    </Card>
  );
};
